import React, { useEffect, useState } from 'react'
import styles from './pending.module.css'

// The pending-state rule (h 3975-4002). A control is disabled on the first
// press and says nothing for the first second; only a wait that becomes real
// draws a pending state: a spinner and the button's own verb in the present
// tense ("Saving…", "Applying…"), never a generic "Loading". A spinner that
// flashes for 80ms is worse than none, and an enabled button under a write in
// flight is how a payroll export gets sent twice.
//
// A panel under a save stays open with everything typed in it: it takes no
// presses at once and dims once the wait is real (`inertClass`), and the layer
// stack will not close it while the write is out (useLayer's `busy`).

export const PENDING_AFTER_MS = 1000

// True once `busy` has lasted past the threshold.
export function useSlow(busy: boolean, ms = PENDING_AFTER_MS) {
  const [slow, setSlow] = useState(false)
  useEffect(() => {
    if (!busy) {
      setSlow(false)
      return
    }
    const t = setTimeout(() => setSlow(true), ms)
    return () => clearTimeout(t)
  }, [busy, ms])
  return busy && slow
}

export function Spinner() {
  return <span className={styles.spin} aria-hidden="true" data-testid="spinner" />
}

type ButtonProps = React.ButtonHTMLAttributes<HTMLButtonElement> & {
  // The write this button started is in flight.
  busy: boolean
  // What it says once the wait is real: its own verb, "Saving…".
  pending: string
}

// A button that follows the rule: disabled at once, its label unchanged for a
// second, then a spinner and `pending`.
export function PendingButton({ busy, pending, disabled, children, type = 'button', ...rest }: ButtonProps) {
  const slow = useSlow(busy)
  return (
    <button {...rest} type={type} disabled={disabled || busy} aria-busy={busy || undefined}>
      {slow ? (
        <>
          <Spinner />
          {pending}
        </>
      ) : (
        children
      )}
    </button>
  )
}

// The class for a panel body under a write in flight: no presses land on it
// at once, and it dims only once the wait is real.
export function inertClass(busy: boolean, slow: boolean) {
  return busy ? `${styles.busy}${slow ? ` ${styles.slow}` : ''}` : ''
}

// A write that failed (h 4063-4070): a red panel directly under the button
// that failed, saying what did not happen and what to do. It goes in the
// button's own row, after the button, and takes a line of its own beneath it;
// the form above is left exactly as it was, so nothing typed is lost.
export function Failed({ text, testId = 'failed' }: { text: string | null | undefined; testId?: string }) {
  if (!text) return null
  return (
    <div role="alert" data-testid={testId} className={styles.failed}>
      {text}
    </div>
  )
}

// Skeleton rows (h 3994-4002): while a list loads, its body is rows in the
// table's own grid (the row class it already uses), one shimmering bar per
// column, so the shape says what is coming and the layout does not jump when
// it lands. The header, filters and chips around it stay live. `rowStyle` is
// for a table whose rows take their grid inline (Reports, Wages).
export function SkeletonRows({
  rowClass,
  cols,
  rows = 8,
  label = 'Loading',
  rowStyle,
}: {
  rowClass: string
  cols: number
  rows?: number
  label?: string
  rowStyle?: React.CSSProperties
}) {
  return (
    <div role="status" aria-label={label} data-testid="skeleton-rows">
      {Array.from({ length: rows }, (_, i) => (
        <div key={i} className={`${rowClass} ${styles.skelRow}`} style={rowStyle} aria-hidden="true">
          {Array.from({ length: cols }, (_, j) => (
            <span key={j} className={styles.bar} style={{ animationDelay: `${i * 60}ms` }} />
          ))}
        </div>
      ))}
    </div>
  )
}

// The same for a list drawn as a real <table>: a <tbody> of skeleton rows in
// place of the table's own, so the <thead> and its columns stay as they are.
export function SkeletonTableRows({ cols, rows = 8, label = 'Loading', rowClass = '' }: { cols: number; rows?: number; label?: string; rowClass?: string }) {
  return (
    <tbody role="status" aria-label={label} data-testid="skeleton-rows">
      {Array.from({ length: rows }, (_, i) => (
        <tr key={i} className={`${rowClass} ${styles.skelTr}`} aria-hidden="true">
          {Array.from({ length: cols }, (_, j) => (
            <td key={j}>
              <span className={styles.bar} style={{ animationDelay: `${i * 60}ms` }} />
            </td>
          ))}
        </tr>
      ))}
    </tbody>
  )
}
